import {useMutation, useQuery} from '@apollo/client';
import {useSnackbar} from 'notistack';
import React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {DELETE_TASK, LIST_TASKS} from '../tasksQueries';
import {ListTasks, ListTasksVariables} from '../../../schema-types/ListTasks';
import EditorGrid from '../../../components/EditorGrid/EditorGrid';
import {tasksColumns} from './tasksColumns';
import {styles} from './styles';
import {SaltusAny} from '../../../utils/SaltusAny';
import validateUUID from '../../../utils/validateUUID';

export const filterHandoverTasks = <T extends {subject: string | null}>(
  tasks: T[]
): T[] => tasks.filter((task) => task.subject !== 'Handover');

export const TasksGrid: React.FC<{
  onSelected?: (args: SaltusAny) => SaltusAny;
  minHeight?: number;
  readOnly?: boolean;
}> = ({onSelected, minHeight, readOnly}) => {
  const navigate = useNavigate();
  const {reviewId} = useParams();
  const {data, error, loading} = useQuery<ListTasks, ListTasksVariables>(LIST_TASKS, {
    variables: {
      reviewId: validateUUID(reviewId),
    },
    fetchPolicy: 'network-only',
  });
  const classes = styles();
  const {enqueueSnackbar} = useSnackbar();
  const tasks = filterHandoverTasks(data?.tasks || []);

  const [deleteTask] = useMutation(DELETE_TASK, {
    // eslint-disable-next-line consistent-return
    onCompleted: ({deleteTask: deleteTaskData}) => {
      if (!deleteTaskData.ok) {
        return enqueueSnackbar('Unable to delete the task.', {
          variant: 'error',
          autoHideDuration: null,
        });
      }
      enqueueSnackbar('The selected task has been deleted.', {
        variant: 'success',
      });
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete the task. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
    update(cache, {data: {deleteTask: deleteTaskData}}) {
      if (!deleteTaskData.ok) {
        return;
      }
      cache.modify({
        fields: {
          tasks(existingTasks, {readField}) {
            return existingTasks.filter(
              (n: SaltusAny) => readField('id', n) !== deleteTaskData.taskId
            );
          },
        },
      });
    },
  });

  const deleteSelectedTask = (taskId: string) =>
    deleteTask({
      variables: {
        taskId,
      },
    });

  return (
    <div style={classes.root}>
      <EditorGrid
        dataGridId="TasksGrid"
        sx={classes.dataGridRoot}
        columns={tasksColumns}
        rows={tasks}
        error={error}
        onRowDoubleClick={(params) => {
          navigate({pathname: `../tasks-builder/${params.id}`});
        }}
        hideFooter
        loading={loading}
        onSelected={onSelected}
        deleteFunc={readOnly ? undefined : deleteSelectedTask}
        minHeight={minHeight ?? '600px'}
        createFunc={false}
        createFuncLoading={false}
        syncFunc={() => {}}
        updateFunc={false}
      />
    </div>
  );
};

export default TasksGrid;
